import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { APIProvider } from "@vis.gl/react-google-maps";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatInputField } from "blocks-nextjs/blocks-react/components/InputField/InputField";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
import { CaasRoute } from "../../routes";
import { useCreateProjectMutate, useGetProjectDetailsQuery } from "../../services/query/create-project-api.query";
import { useUpdateProjectDetailsMutate } from "../../services/query/update-project-details.query";
import { DEFAULT_PROJECT_TIMEZONE } from "../../shared/constants";
import useAppConfigStore from "../../store/useAppConfigStore";
import useGlobalStore from "../../store/useGlobalStore";
import useNewProjectStore from "../../store/useNewProjectStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
import GoogleMapLocator from "../GoogleMaps/GoogleMapLocator";
import PlaceAutocomplete from "../GoogleMaps/PlaceAutoComplete";
import NewProjectModal from "./NewProjectModal";
const API_KEY = getGoogleMapsApiKey();
const ZOOM_LEVEL_OFFSET = 0.07;
const FALLBACK_OFFSET = 0.01;
export var ModeType;
(function (ModeType) {
    ModeType["CREATE"] = "create";
    ModeType["UPDATE"] = "update";
})(ModeType || (ModeType = {}));
const NewProjectForm = ({ activeStep, setActiveStepHandler }) => {
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [isModalActive, setIsModalActive] = useState(false);
    const debounceTimer = useRef(null);
    const { modeType, projectId, setCompletedStep, setCoordinates, setModeType, setProjectAddress, setProjectAddressLat, setProjectAddressLng, setProjectId, setProjectName, setProjectOwner, setProjectStatus, setReset } = useNewProjectStore((state) => ({
        modeType: state.modeType,
        projectId: state.projectId,
        setCompletedStep: state.setCompletedStep,
        setCoordinates: state.setCoordinates,
        setModeType: state.setModeType,
        setProjectAddress: state.setProjectAddress,
        setProjectAddressLat: state.setProjectAddressLat,
        setProjectAddressLng: state.setProjectAddressLng,
        setProjectId: state.setProjectId,
        setProjectName: state.setProjectName,
        setProjectOwner: state.setProjectOwner,
        setProjectStatus: state.setProjectStatus,
        setReset: state.setReset
    }));
    const { primaryPartyType, setGlobalNotification, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        setGlobalNotification: state.setGlobalNotification,
        userPartyNumber: state.userPartyNumber
    }));
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const location = useLocation();
    const getProjectDetails = location.state;
    const projectInfo = getProjectDetails?.project;
    const queryParamsGetProjectInfo = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const { data: projectDetailsResponse, refetch } = useGetProjectDetailsQuery(projectInfo?.projectId || projectId, queryParamsGetProjectInfo);
    const [selectedPinInfo, setSelectedPinInfo] = useState(null);
    const [defaultValuesInfo, setDefaultValuesInfo] = useState({
        coordinates: [],
        modeType: ModeType.CREATE,
        owner: "",
        projectAddress: "",
        projectAddressLat: "",
        projectAddressLng: "",
        projectName: ""
    });
    useEffect(() => {
        if (projectDetailsResponse) {
            setProjectId(projectDetailsResponse.projectId);
            setProjectStatus(projectDetailsResponse.projectStatus);
            setProjectName(projectDetailsResponse.projectName);
            setProjectOwner(projectDetailsResponse.owner);
            setProjectAddress(projectDetailsResponse.streetAddress);
            setModeType(ModeType.UPDATE);
            if (projectDetailsResponse?.coordinates && projectDetailsResponse?.coordinates.length !== 0) {
                setProjectAddressLat(projectDetailsResponse?.coordinates[1].toString());
                setProjectAddressLng(projectDetailsResponse?.coordinates[0].toString());
                setCoordinates([
                    { lat: projectDetailsResponse?.coordinates[1], lng: projectDetailsResponse?.coordinates[0] }
                ]);
            }
            // Create selectedPlace object for existing project
            if (projectDetailsResponse.streetAddress &&
                projectDetailsResponse.coordinates &&
                projectDetailsResponse.coordinates.length > 1) {
                const lat = Number(projectDetailsResponse.coordinates[1]);
                const lng = Number(projectDetailsResponse.coordinates[0]);
                // Check if google.maps is available and create proper LatLng
                let location;
                let viewport;
                if (typeof google !== "undefined" && google.maps?.LatLng && google.maps?.LatLngBounds) {
                    location = new google.maps.LatLng(lat, lng);
                    // Create a viewport (bounds) around the location for proper zooming
                    const offset = ZOOM_LEVEL_OFFSET; // Adjust this value to control zoom level (smaller = more zoomed in)
                    const sw = new google.maps.LatLng(lat - offset, lng - offset);
                    const ne = new google.maps.LatLng(lat + offset, lng + offset);
                    viewport = new google.maps.LatLngBounds(sw, ne);
                }
                else {
                    // Fallback to coordinate literal
                    location = { lat, lng };
                    // Fallback viewport
                    const offset = FALLBACK_OFFSET;
                    viewport = {
                        east: lng + offset,
                        north: lat + offset,
                        south: lat - offset,
                        west: lng - offset
                    };
                }
                const selectedPlaceResponse = {
                    formatted_address: projectDetailsResponse.streetAddress,
                    geometry: {
                        location: location,
                        viewport: viewport
                    },
                    name: projectDetailsResponse.projectName,
                    place_id: `existing_${projectDetailsResponse.projectId}`
                };
                setSelectedPlace(selectedPlaceResponse);
            }
            setDefaultValuesInfo({
                coordinates: Array.isArray(projectDetailsResponse?.coordinates) && projectDetailsResponse.coordinates.length > 1
                    ? [
                        {
                            lat: Number(projectDetailsResponse.coordinates[1]),
                            lng: Number(projectDetailsResponse.coordinates[0])
                        }
                    ]
                    : [],
                modeType: ModeType.UPDATE,
                owner: projectDetailsResponse?.owner ?? "",
                projectAddress: projectDetailsResponse?.streetAddress ?? "",
                projectAddressLat: Array.isArray(projectDetailsResponse?.coordinates) && projectDetailsResponse.coordinates.length > 1
                    ? projectDetailsResponse.coordinates[1].toString()
                    : "",
                projectAddressLng: Array.isArray(projectDetailsResponse?.coordinates) && projectDetailsResponse.coordinates.length > 1
                    ? projectDetailsResponse.coordinates[0].toString()
                    : "",
                projectName: projectDetailsResponse?.projectName ?? ""
            });
        }
    }, [projectDetailsResponse]);
    useEffect(() => {
        if (projectInfo?.projectId || (projectId && modeType === ModeType.UPDATE)) {
            refetch();
        }
    }, [projectInfo, projectId]);
    const NewProjectSchema = z.object({
        coordinates: z.array(z.object({
            lat: z
                .number()
                .min(-90, { message: "Latitude must be between -90 and 90" })
                .max(90, { message: "Latitude must be between -90 and 90" }),
            lng: z
                .number()
                .min(-180, { message: "Longitude must be between -180 and 180" })
                .max(180, { message: "Longitude must be between -180 and 180" })
        })),
        modeType: z.enum(["create", "update"]).default(ModeType.CREATE),
        owner: z
            .string()
            .trim()
            .min(1, { message: "Enter owner" })
            .max(100, { message: "Maximum 100 characters only allowed" }),
        projectAddress: z.string().trim().min(1, { message: "Enter Address" }),
        projectAddressLat: z
            .string()
            .min(1, { message: "Latitude is required" })
            .superRefine((val, ctx) => {
            const num = Number(val.trim());
            if (!Number.isFinite(num)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Latitude must be a valid number"
                });
            }
            if (num < -90 || num > 90) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Latitude must be between -90 and 90"
                });
            }
        }),
        projectAddressLng: z
            .string()
            .min(1, { message: "Longitude is required" })
            .superRefine((val, ctx) => {
            const num = Number(val.trim());
            if (!Number.isFinite(num)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Longitude must be a valid number"
                });
            }
            if (num < -180 || num > 180) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Longitude must be between -180 and 180"
                });
            }
        }),
        projectName: z
            .string()
            .trim()
            .min(1, { message: "Enter project name" })
            .max(20, { message: "Maximum 20 characters only allowed" })
    });
    const { control, formState: { errors, isDirty, isValid }, getValues, handleSubmit, reset, setError, setValue, watch } = useForm({
        defaultValues: defaultValuesInfo,
        mode: "onBlur",
        resolver: zodResolver(NewProjectSchema)
    });
    const navigate = useNavigate();
    useEffect(() => {
        if (defaultValuesInfo) {
            reset(defaultValuesInfo);
        }
    }, [defaultValuesInfo]);
    const createProjectQueryParams = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const { mutate: createMutate } = useCreateProjectMutate(createProjectQueryParams);
    const queryParamsUpdateProject = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const { mutate: updateMutate } = useUpdateProjectDetailsMutate(projectId, queryParamsUpdateProject);
    useEffect(() => {
        if (selectedPlace?.formatted_address) {
            setValue("projectAddress", selectedPlace?.formatted_address);
            setProjectAddress(selectedPlace?.formatted_address);
        }
        if (selectedPlace?.geometry && selectedPlace?.geometry?.location) {
            const location = selectedPlace.geometry.location;
            if (typeof location.lat === "function" && typeof location.lng === "function") {
                const lat = selectedPlace.geometry.location?.lat();
                const lng = selectedPlace.geometry.location?.lng();
                if (lat && lng) {
                    setValue("projectAddressLat", lat.toString(), { shouldDirty: true, shouldValidate: true });
                    setValue("projectAddressLng", lng.toString(), { shouldDirty: true, shouldValidate: true });
                    setProjectAddressLat(lat.toString());
                    setProjectAddressLng(lng.toString());
                    setCoordinates([{ lat, lng }]);
                    setValue("coordinates", [{ lat, lng }]);
                }
            }
        }
    }, [selectedPlace]);
    const getMutateParam = (formData) => {
        return {
            onError: (error) => {
                const errorResponse = error.response?.data ||
                    { code: error?.code, description: error?.message };
                const errorStatus = error?.status || error?.code || "Unknown Error";
                const errorMessage = ("data" in errorResponse ? errorResponse.data?.description : errorResponse.description) ||
                    error?.message;
                const ERROR_PROJECT_EXISTS = "Project already exists";
                const ERROR_PROJECT_NAME_EXISTS = "Project name already exists";
                const ERROR_STATUS_CODE_400 = 400;
                if ((errorStatus === ERROR_STATUS_CODE_400 && errorMessage === ERROR_PROJECT_EXISTS) ||
                    errorMessage === ERROR_PROJECT_NAME_EXISTS) {
                    setError("projectName", {
                        message: "Project name already exists.Please select another name",
                        type: "manual"
                    });
                }
                else {
                    setGlobalNotification({
                        message: `${errorMessage}.`,
                        showNotification: true,
                        type: Variant.error
                    });
                }
            },
            onSuccess: (data) => {
                if (getValues("modeType") === ModeType.CREATE) {
                    if (data?.projectId) {
                        setProjectId(data?.projectId);
                        setProjectStatus("PENDING");
                        setModeType(ModeType.UPDATE);
                        setDefaultValuesInfo({
                            coordinates: [
                                { lat: Number(formData.projectAddressLat), lng: Number(formData.projectAddressLng) }
                            ],
                            modeType: ModeType.UPDATE,
                            owner: formData.owner,
                            projectAddress: formData.projectAddress,
                            projectAddressLat: formData.projectAddressLat,
                            projectAddressLng: formData.projectAddressLng,
                            projectName: formData.projectName
                        });
                    }
                }
                if (getValues("modeType") === ModeType.UPDATE) {
                    setDefaultValuesInfo({
                        ...defaultValuesInfo,
                        coordinates: formData.projectAddressLat && formData.projectAddressLng
                            ? [{ lat: Number(formData.projectAddressLat), lng: Number(formData.projectAddressLng) }]
                            : defaultValuesInfo.coordinates,
                        modeType: ModeType.UPDATE,
                        owner: formData.owner ?? defaultValuesInfo.owner,
                        projectAddress: formData.projectAddress ?? defaultValuesInfo.projectAddress,
                        projectAddressLat: formData.projectAddressLat ?? defaultValuesInfo.projectAddressLat,
                        projectAddressLng: formData.projectAddressLng ?? defaultValuesInfo.projectAddressLng,
                        projectName: formData.projectName ?? defaultValuesInfo.projectName
                    });
                }
                setActiveStepHandler(activeStep + 1);
                setCompletedStep(activeStep);
            }
        };
    };
    const onSubmit = (formData) => {
        if (getValues("modeType") === ModeType.UPDATE) {
            const isProjectNameChanged = formData.projectName !== defaultValuesInfo.projectName;
            const isProjectOwnerChanged = formData.owner !== defaultValuesInfo.owner;
            const isProjectAddressChanged = formData.projectAddress !== defaultValuesInfo.projectAddress;
            const isProjectAddressLatChanged = formData.projectAddressLat !== defaultValuesInfo.projectAddressLat;
            const isProjectAddressLngChanged = formData.projectAddressLng !== defaultValuesInfo.projectAddressLng;
            const updateProjectDetails = {
                ...(isProjectNameChanged && { projectName: formData.projectName }),
                ...(isProjectOwnerChanged && { owner: formData.owner }),
                ...(isProjectAddressChanged && { projectAddress: formData.projectAddress }),
                ...(isProjectAddressLatChanged &&
                    isProjectAddressLngChanged && {
                    coordinates: [formData.projectAddressLng, formData.projectAddressLat]
                })
            };
            const requestData = {
                ...(isProjectNameChanged && { projectName: formData.projectName }),
                ...(isProjectOwnerChanged && { owner: formData.owner }),
                ...(isProjectAddressChanged && { projectAddress: formData.projectAddress }),
                ...(isProjectAddressLatChanged && { projectAddressLat: formData.projectAddressLat }),
                ...(isProjectAddressLngChanged && { projectAddressLng: formData.projectAddressLng })
            };
            if (Object.keys(updateProjectDetails).length === 0) {
                setActiveStepHandler(activeStep + 1);
                setCompletedStep(activeStep);
                return;
            }
            else {
                updateMutate(updateProjectDetails, getMutateParam(requestData));
            }
        }
        if (getValues("modeType") === ModeType.CREATE) {
            const createProjectDetails = {
                owner: formData.owner,
                projectAddress: formData.projectAddress,
                projectName: formData.projectName,
                ...(formData.projectAddressLat &&
                    formData.projectAddressLng && {
                    coordinates: [formData.projectAddressLng, formData.projectAddressLat]
                }),
                ...(appConfig?.release_1_1?.showCestTimezone && {
                    timezone: DEFAULT_PROJECT_TIMEZONE
                })
            };
            const requestData = {
                owner: formData.owner,
                projectAddress: formData.projectAddress,
                projectAddressLat: formData.projectAddressLat,
                projectAddressLng: formData.projectAddressLng,
                projectName: formData.projectName
            };
            createMutate(createProjectDetails, getMutateParam(requestData));
        }
    };
    const onModalClose = (modalState) => {
        setIsModalActive(modalState);
    };
    useEffect(() => {
        return () => {
            const { pathname } = window.location;
            if (pathname !== CaasRoute.NewProjectPage) {
                setReset();
            }
            // Cleanup debounce timer
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);
    useEffect(() => {
        if (selectedPinInfo) {
            setValue("projectAddress", selectedPinInfo.address, { shouldDirty: true, shouldValidate: true });
            setValue("projectAddressLat", selectedPinInfo.coordinates.lat.toString(), {
                shouldDirty: true,
                shouldValidate: true
            });
            setValue("projectAddressLng", selectedPinInfo.coordinates.lng.toString(), {
                shouldDirty: true,
                shouldValidate: true
            });
            setValue("coordinates", [{ lat: selectedPinInfo?.coordinates.lat, lng: selectedPinInfo?.coordinates.lng }], { shouldDirty: true, shouldValidate: true });
            setProjectAddress(selectedPinInfo.address);
            setProjectAddressLat(selectedPinInfo.coordinates.lat.toString());
            setProjectAddressLng(selectedPinInfo.coordinates.lng.toString());
            setCoordinates([{ lat: selectedPinInfo?.coordinates.lat, lng: selectedPinInfo?.coordinates.lng }]);
            setSelectedPinInfo(null);
        }
    }, [selectedPinInfo]);
    const getFormattedAddress = useCallback((geoCodes) => {
        if (geoCodes && geoCodes.length > 0) {
            const addressList = geoCodes.filter((geoCode) => geoCode?.address_components?.length > 1 && geoCode?.formatted_address);
            const formattedAddress = addressList?.[0]?.formatted_address || null;
            if (geoCodes[0].address_components.length > 1 && formattedAddress !== null) {
                const addressSegments = formattedAddress?.split(" ");
                const selectedAddress = addressSegments?.slice(1, addressSegments?.length).join(" ");
                return selectedAddress;
            }
            return formattedAddress;
        }
        return null;
    }, []);
    const reverseGeocodeFromCoordinates = useCallback((lat, lng) => {
        if (!lat || !lng || lat === 0 || lng === 0)
            return;
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            return;
        }
        if (typeof google !== "undefined" && google.maps?.Geocoder) {
            const geocoder = new google.maps.Geocoder();
            const latLng = new google.maps.LatLng(lat, lng);
            geocoder.geocode({ location: latLng }, (results, status) => {
                if (status === "OK" && results && results.length > 0) {
                    const formattedAddress = getFormattedAddress(results);
                    if (formattedAddress) {
                        setValue("projectAddress", formattedAddress);
                        setProjectAddress(formattedAddress);
                        const location = new google.maps.LatLng(lat, lng);
                        const offset = ZOOM_LEVEL_OFFSET;
                        const sw = new google.maps.LatLng(lat - offset, lng - offset);
                        const ne = new google.maps.LatLng(lat + offset, lng + offset);
                        const viewport = new google.maps.LatLngBounds(sw, ne);
                        const selectedPlaceFromCoords = {
                            formatted_address: formattedAddress,
                            geometry: {
                                location: location,
                                viewport: viewport
                            },
                            name: formattedAddress,
                            place_id: `coords_${lat}_${lng}`
                        };
                        setSelectedPlace(selectedPlaceFromCoords);
                    }
                }
            });
        }
    }, [setValue, setProjectAddress, getFormattedAddress]);
    const handleLatitudeChange = useCallback((value) => {
        setProjectAddressLat(value.toString());
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        const currentLng = Number(watch("projectAddressLng"));
        if (value && currentLng && value !== 0 && currentLng !== 0) {
            debounceTimer.current = setTimeout(() => {
                reverseGeocodeFromCoordinates(value, currentLng);
            }, 1000);
        }
    }, [setValue, setProjectAddressLat, watch, reverseGeocodeFromCoordinates]);
    const handleLongitudeChange = useCallback((value) => {
        setProjectAddressLng(value.toString());
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        const currentLat = Number(watch("projectAddressLat"));
        if (value && currentLat && value !== 0 && currentLat !== 0) {
            debounceTimer.current = setTimeout(() => {
                reverseGeocodeFromCoordinates(currentLat, value);
            }, 1000);
        }
    }, [setValue, setProjectAddressLng, watch, reverseGeocodeFromCoordinates]);
    const triggerSubmit = () => {
        handleSubmit(onSubmit)();
    };
    const isAnyFieldValueChanged = () => {
        const currentValues = getValues();
        const isProjectNameChanged = currentValues.projectName?.trim() !== defaultValuesInfo.projectName;
        const isProjectOwnerChanged = currentValues.owner?.trim() !== defaultValuesInfo.owner;
        const isProjectAddressChanged = currentValues.projectAddress?.trim() !== defaultValuesInfo.projectAddress;
        const isProjectAddressLatChanged = currentValues.projectAddressLat?.trim() !== defaultValuesInfo.projectAddressLat;
        const isProjectAddressLngChanged = currentValues.projectAddressLng?.trim() !== defaultValuesInfo.projectAddressLng;
        return (isProjectNameChanged ||
            isProjectOwnerChanged ||
            isProjectAddressChanged ||
            isProjectAddressLatChanged ||
            isProjectAddressLngChanged);
    };
    const isNextOrUpdateDisabled = () => {
        const modeType = getValues("modeType");
        if (modeType === ModeType.CREATE) {
            return !isValid || !isDirty;
        }
        if (modeType === ModeType.UPDATE) {
            return !isValid;
        }
        return true;
    };
    return (_jsxs("div", { className: "flex-1 flex flex-col gap-[8px]", children: [_jsxs("div", { className: "flex-1 flex", children: [_jsx("div", { className: "flex-[1_1_40%]  flex flex-col border-[#CCCCCC] border-t border-l border-b ", children: _jsx(GoogleMapLocator, { selectedPlace: selectedPlace, setSelectedPinInfo: (pinInfo) => setSelectedPinInfo(pinInfo) }) }), _jsxs("div", { className: "flex-1 flex flex-col gap-[16px] bg-[#F6F6FA] rounded-r-[4px] border pt-[16px] pr-[16px] pl-[16px] pb-[20px] min-h-0", children: [_jsx("div", { className: "font-bold text-base  tracking-normal", children: "Project Details" }), _jsx("div", { className: "font-normal not-italic text-sm leading-5 tracking-normal text-[#666565]", "data-test": "new-project-form-instruction", children: "Please complete the project work site information to create a new record." }), _jsx("form", { "data-test": "new-project-form", onSubmit: (e) => e.preventDefault(), children: _jsxs("div", { className: "flex flex-col gap-2 rounded-lg", children: [_jsx(Controller, { control: control, name: "projectName", render: ({ field: { onBlur, onChange, ref, value } }) => (_jsx(CatInputField, { ariaDescribedBy: "text-field-aria-1", "data-test": "project-name-field", fieldId: "text-field-1", label: "Project Name", onBlChange: (e) => {
                                                    onChange(e);
                                                    setProjectName(e.detail.value);
                                                }, onBlur: onBlur, placeholder: "Enter project name", ref: ref, required: true, size: "lg", value: value, ...(errors.projectName && {
                                                    errorNote: errors.projectName.message,
                                                    status: "error"
                                                }) })) }), _jsx(APIProvider, { apiKey: API_KEY, children: _jsx("div", { children: _jsx(PlaceAutocomplete, { control: control, "data-test": "project-address-field", errors: errors, onPlaceSelect: setSelectedPlace }) }) }), _jsxs("div", { className: "flex  gap-3", children: [_jsx("div", { className: "basis-[50%]", children: _jsx(Controller, { control: control, name: "projectAddressLat", render: ({ field: { onBlur, onChange, ref, value } }) => (_jsx(CatInputField, { ariaDescribedBy: "text-field-aria-1", "data-test": "project-address-latitude-field", fieldId: "text-field-1", label: "Latitude (X)", onBlChange: (e) => {
                                                                onChange(e);
                                                                if (e.detail.value.trim() !== "") {
                                                                    const latValue = Number(e.detail.value);
                                                                    handleLatitudeChange(latValue);
                                                                }
                                                            }, onBlur: onBlur, placeholder: "Enter latitude", ref: ref, required: true, size: "lg", value: value, ...(errors.projectAddressLat && {
                                                                errorNote: errors.projectAddressLat.message,
                                                                status: "error"
                                                            }) })) }) }), _jsx("div", { className: "basis-[50%]", children: _jsx(Controller, { control: control, name: "projectAddressLng", render: ({ field: { onBlur, onChange, ref, value } }) => (_jsx(CatInputField, { ariaDescribedBy: "text-field-aria-1", "data-test": "project-address-longitude-field", fieldId: "text-field-1", label: "Longitude (Y)", onBlChange: (e) => {
                                                                onChange(e);
                                                                if (e.detail.value.trim() !== "") {
                                                                    handleLongitudeChange(Number(e.detail.value));
                                                                }
                                                            }, onBlur: onBlur, placeholder: "Enter longitude", ref: ref, required: true, size: "lg", value: value, ...(errors.projectAddressLng && {
                                                                errorNote: errors.projectAddressLng.message,
                                                                status: "error"
                                                            }) })) }) })] }), _jsx(Controller, { control: control, name: "owner", render: ({ field: { onBlur, onChange, ref, value } }) => (_jsx(CatInputField, { ariaDescribedBy: "text-field-aria-1", "data-test": "project-owner-field", fieldId: "text-field-1", label: "Owner", onBlChange: (e) => {
                                                    onChange(e);
                                                    setProjectOwner(e.detail.value);
                                                }, onBlur: onBlur, placeholder: "Enter owner name", ref: ref, required: true, size: "lg", value: value, ...(errors.owner && {
                                                    errorNote: errors.owner.message,
                                                    status: "error"
                                                }) })) })] }) }), _jsx("div", { children: _jsx(NewProjectModal, { "data-test": "new-project-form-modal", onModalClose: onModalClose, showModal: isModalActive }) })] })] }), _jsxs("div", { className: "flex gap-[8px] justify-end pl-[40px] pr-[40px]", children: [_jsx(CatButton, { "data-test": "new-project-form-cancel-button", onClick: () => {
                            const isCancelModalShow = (isValid && isDirty) || getValues("modeType") === ModeType.UPDATE;
                            if (isCancelModalShow) {
                                setIsModalActive(true);
                            }
                            else {
                                setReset();
                                navigate(CaasRoute.Home);
                            }
                        }, size: "sm", children: "Cancel" }), _jsx(CatButton, { "data-test": "new-project-form-next-button", disabled: isNextOrUpdateDisabled(), onClick: () => {
                            triggerSubmit();
                        }, size: "sm", type: "submit", variant: "primary", children: `${getValues("modeType") === ModeType.UPDATE && isAnyFieldValueChanged() ? "Update" : "Next"}` })] })] }));
};
export default NewProjectForm;
